import {
  Component,
  inject,
  input,
  output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { PiezeoService } from "../../../../app/services/piezeo.service";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  checkSamePiezeoSerial,
  compareSameAxis,
  compareValidator,
  duplicateChannelsValidator,
  notEqualValidator,
} from "../../../../app/directives/form-common-validators";
import { MatRadioButton } from "@angular/material/radio";
import { BaseUrlConstantsService } from "../../../../app/services/base-url-constants.service";

interface Sensor {
  assetType: string;
  subType: string;
  assetMarker: string;
  sensorMarker: string;
  sensorDNames: string;
  sensor_number: number;
  x: number;
  y: number;
  xPercentage: number;
  xPercentages: number;
  xPercentageNet: number;
  yPercentage: number;
  yPercentages: number;
  yPercentageNet: number;
  color?: string;
  asset_status?: number;
}

interface SubsystemData {
  subsystem_name: string;
  client_id: string;
  Image_path: string;
  subsystem_type: string;
}

interface PowertrainData {
  data: Sensor[];
  suybsystem_table_data: SubsystemData;
}

@Component({
  selector: "app-piezeo-modal",
  templateUrl: "./piezeo-modal.component.html",
  styleUrls: ["./piezeo-modal.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    ReactiveFormsModule,
    MatFormFieldModule, // Add MatFormFieldModule
    MatSelectModule, // Add MatSelectModule
    MatInputModule,
    FormsModule,
    MatRadioButton,
  ],
})
export class PiezeoModalComponent implements AfterViewInit {
  @ViewChild("imageContainer") imageContainer: ElementRef;
  @ViewChild("powertrainImage") powertrainImage: ElementRef;

  // SERVICES
  piezeo_service = inject<any>(PiezeoService);

  // INPUTS & OUTPUTS
  public modal_type = input.required<string>();
  public close_modal = output<void | any | null>();
  public gateway_data = input<any>();
  current_piezeo: any = input<any>();
  current_gateway: any = input<any>();

  // SIGNALS for reactive data
  public powertrainName = signal<string>("");
  public peizeoImg = signal<string>("");
  public sensors = signal<Sensor[]>([]);
  public subsystemType = signal<string>("");
  public isImageLoaded = signal<boolean>(false);

  //GATEWAY VARIABLES

  gatewayConfigForm = new FormGroup(
    {
      gatewayType: new FormControl("type_a", Validators.required),
      gatewayAddress: new FormControl<null | any>({}, [Validators.required]),
      summaryFileRefreshRate: new FormControl(12, {
        validators: [Validators.required, Validators.min(1)],
      }),
      fullRefreshFileRate: new FormControl(23, [
        Validators.required,
        Validators.min(1),
      ]),
      anomalyAnalysisTime: new FormControl(60, [Validators.required]),
      anomalyCounter: new FormControl(1, [Validators.required]),
      scanRate: new FormControl(60, [Validators.required]),
      odr_value: new FormControl("Normal", [Validators.required]),
      asset_threshold_status: new FormControl("0.1", [Validators.required]),
    },
    {
      validators: [
        notEqualValidator("summaryFileRefreshRate", "fullRefreshFileRate"),
        compareValidator("summaryFileRefreshRate", "fullRefreshFileRate"),
      ],
    },
  );

  // Options for Gateway Type dropdown
  gatewayTypes = [];
  gateway_loader = true;

  // Options for Gateway Address dropdown (example addresses)
  gatewayAddresses: any[] = [];
  configured_gateways = []; // to hold configured gateways

  piezeoConfigForm = new FormGroup({
    no_of_axis: new FormControl(0, [Validators.required, Validators.min(1)]),
    sensors_info: new FormGroup({}),
    temperature_channel: new FormControl("", [Validators.required]),
  });
  total_no_of_axis = [];
  channel_numbers = [1, 2, 3, 4, 5, 6];
  available_channels = [1, 2, 3, 4, 5, 6];
  // odr_options = [
  //   { value: "High", name: "High(3400Hz)" },
  //   { value: "Normal", name: "Normal(1700Hz)" },
  //   { value: "Low", name: "Low(200Hz)" },
  //   { value: "ExtraLow", name: "Extra Low(100Hz)" },
  //   { value: "ExtraExtraLow", name: "Extra Extra Low(50Hz)" },
  //   { value: "UltraLow", name: "Ultra Low" },
  //   { value: "UltraUltraLow", name: "Ultra Ultra Low" },
  // ];

  odr_options = [
    { name: "High (24000Hz)", value: "High" },
    { name: "Normal (18000Hz)", value: "Normal" },
    { name: "Low (10000Hz)", value: "Low" },
    { name: "Extra Low (3000Hz)", value: "ExtraLow" },
    { name: "Extra Low (1000Hz)", value: "ExtraExtraLow" },
    { name: "Ultra Low (500Hz)", value: "UltraLow" },
    { name: "Ultra Ultra Low (100Hz)", value: "UltraUltraLow" },
  ];
  //COMMON CONFIGS
  all_sensors = [];
  current_sensor: any = {};
  open_axis_modal = false;
  no_of_axis: number = 1;
  is_temperature_added: number = 0;
  filtered_sensors = [];

  constructor(private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.configured_gateways = this.piezeo_service.getConfiguredGateways();

    switch (this.modal_type()) {
      case "pt_preview":
        this.loadPiezeoData();
        break;
      case "associate_device_to_gateway":
        this.loadAllSensors();
        this.filterSensors();
        break;
      case "add_gateway":
        this.fetchGatewayByDevice();
        break;
      case "edit_gateway":
        this.fetchGatewayByDevice();
        let client_id = localStorage.getItem("user_id");
        setTimeout(() => {
          this.handleGatewayDeviceChange(
            this.current_gateway().gatewayType,
            client_id,
          );
        }, 1000);
        break;
      case "piezeo_config":
        this.current_sensor = this.current_piezeo();
        this.setAvailableChannels();
        this.detectAxisChange();
        this.piezeoConfigForm
          .get("no_of_axis")
          .patchValue(this.current_sensor.no_of_axis);
        this.piezeoConfigForm.get("no_of_axis").disable({ onlySelf: true });
        if (this.current_sensor.is_temperature_added == 0)
          this.piezeoConfigForm
            .get("temperature_channel")
            .disable({ onlySelf: true });
        break;
    }
  }

  ngAfterViewInit() {
    // Adjust container aspect ratio based on loaded image
    if (this.isImageLoaded()) {
      this.adjustImageContainer();
    }
  }

  loadPiezeoData() {
    const data: PowertrainData = this.piezeo_service.getData();
    console.log("Powertrain Data:", data);

    if (data && data.suybsystem_table_data) {
      // Set subsystem data
      this.powertrainName.set(data.suybsystem_table_data.subsystem_name);
      this.peizeoImg.set(data.suybsystem_table_data.Image_path);
      this.subsystemType.set(data.suybsystem_table_data.subsystem_type);

      // Set sensors data
      if (data.data && Array.isArray(data.data)) {
        // Process sensors and assign colors/status if not present
        const processedSensors = data.data.map((sensor) => ({
          ...sensor,
          color: sensor.color || this.getDefaultSensorColor(sensor),
          asset_status:
            sensor.asset_status !== undefined ? sensor.asset_status : 1,
        }));

        this.sensors.set(processedSensors);
      }
    }
  }

  getDefaultSensorColor(sensor: Sensor): string {
    // Default color logic - you can customize based on your requirements
    if (sensor.assetMarker === "MASTER") {
      return "#48bb78"; // green
    }
    return "#48bb78"; // default green
  }

  onImageLoad() {
    // When image loads, update the container to match image aspect ratio
    this.isImageLoaded.set(true);
    this.adjustImageContainer();
  }

  adjustImageContainer() {
    if (this.powertrainImage && this.imageContainer) {
      const img = this.powertrainImage.nativeElement;
      const container = this.imageContainer.nativeElement;

      // Wait for image to load
      if (img.complete && img.naturalHeight !== 0) {
        const aspectRatio = (img.naturalHeight / img.naturalWidth) * 100;
        container.style.paddingBottom = `${aspectRatio}%`;
      } else {
        img.onload = () => {
          const aspectRatio = (img.naturalHeight / img.naturalWidth) * 100;
          container.style.paddingBottom = `${aspectRatio}%`;
        };
      }
    }
  }

  getSensorStatus(sensor: Sensor): string {
    // Determine sensor status based on color or other properties
    const color = sensor.color?.toLowerCase() || "";

    if (color.includes("red") || color === "#ff2a1b" || color === "#f56565") {
      return "critical";
    } else if (
      color.includes("yellow") ||
      color.includes("orange") ||
      color === "#faa700" ||
      color === "#ed8936"
    ) {
      return "warning";
    } else if (
      color.includes("green") ||
      color === "#72ff13" ||
      color === "#228b2a" ||
      color === "#48bb78"
    ) {
      return "normal";
    }

    // Default based on asset status
    if (sensor.asset_status === 2) {
      return "critical"; // offline
    }

    return "normal";
  }

  showTooltip(sensor: Sensor): string {
    // Create comprehensive tooltip text from sensor data
    let tooltipText = "";

    if (sensor.sensorDNames) {
      tooltipText += `Sensor: ${sensor.sensorDNames}\n`;
    }

    if (sensor.assetType) {
      tooltipText += `Asset Type: ${sensor.assetType}\n`;
    }

    if (sensor.assetMarker) {
      tooltipText += `Asset Marker: ${sensor.assetMarker}\n`;
    }

    if (sensor.sensorMarker) {
      tooltipText += `Sensor Marker: ${sensor.sensorMarker}\n`;
    }

    if (sensor.sensor_number !== undefined) {
      tooltipText += `Sensor Number: ${
        sensor.sensor_number === 0 ? "BASE" : sensor.sensor_number
      }\n`;
    }

    if (sensor.asset_status !== undefined) {
      const status =
        sensor.asset_status === 2
          ? "Offline"
          : sensor.asset_status === 1
            ? "Online"
            : "Idle";
      tooltipText += `Status: ${status}\n`;
    }

    // Position info
    tooltipText += `\nPosition: (${sensor.xPercentage.toFixed(
      1,
    )}%, ${sensor.yPercentage.toFixed(1)}%)`;

    return tooltipText.trim();
  }

  onSensorClick(sensor: Sensor) {
    // Handle sensor click event
    console.log("Sensor clicked:", sensor);

    // You can emit an event or navigate to sensor details
    // Example: open sensor detail modal, show sensor data, etc.

    // For now, just log the sensor info
    console.log({
      name: sensor.sensorDNames,
      type: sensor.assetType,
      marker: sensor.assetMarker,
      position: {
        x: sensor.xPercentage,
        y: sensor.yPercentage,
      },
      status: sensor.asset_status,
    });
  }

  editPiezo() {
    // Handle edit powertrain action
    console.log("Edit Piezo clicked for:", this.powertrainName());

    // You can emit an event to parent or open edit modal
    // Example: this.edit_powertrain.emit(this.sensors());
  }

  closeModal() {
    this.close_modal.emit(null);
  }

  closePiezeoImgModalBox() {
    // Close the modal
    this.closeModal();
  }

  // --------------------------------------------------- GATEWAY LOGIC STARTS ------------------------------------------

  async fetchGatewayByDevice() {
    let url = `${BaseUrlConstantsService.BASE_PIEZO_SERVICE}/api/get-devices-by-deviceCategoryId/5`;
    this.gateway_loader = true;
    this.gatewayTypes = await this.piezeo_service
      .fetchGatewayDeviceCategories(url)
      .then((res: any) => {
        this.gateway_loader = false;
        return res;
      })
      .catch((error: any) => {
        console.error("Error fetching gateway device categories:", error);
        this.gateway_loader = false;
        return [];
      });

    // TO fill gateway form with already configured values
    if (this.modal_type() == "edit_gateway") this.fillGatewayConfigForm();
  }

  async handleGatewayDeviceChange(id: number, client_id: any) {
    client_id = localStorage.getItem("user_id");
    this.gateway_loader = true;
    this.gatewayAddresses = await this.piezeo_service
      .fetchAssociatedGateways(client_id, id)
      .then((res: any) => {
        this.gateway_loader = false;
        return res;
      })
      .catch((error: any) => {
        console.error("Error fetching associated gateways:", error);
        this.gateway_loader = false;
        return [];
      });
    if (this.modal_type() === "edit_gateway") {
      const g: any = this.gatewayAddresses.find(
        (x) => x.id === this.current_gateway().gatewayAddress.id,
      );

      this.gatewayConfigForm.get("gatewayAddress")!.setValue(g);
    }
  }

  saveGatewayConfig() {
    if (this.modal_type() == "edit_gateway") {
      let old_gid = this.current_gateway().gatewayAddress.id;
      let new_gid = this.gatewayConfigForm.value.gatewayAddress.id;
      let flag = 0;
      this.configured_gateways.map((g) => {
        if (
          g.gatewayAddress.id ==
            (this.gatewayConfigForm.value.gatewayAddress as any).id &&
          g.gatewayAddress.id != old_gid
        ) {
          this.showSnackBar(
            "Gateway Address already configured! Please choose a different one ⚠️.",
          );
          flag = 1;
          return;
        }
      });
      if (flag == 1) return;

      /*
          - this takes old id of the gateway choosen (remain same if gateway not changed) 
          - if new gateway address was seelcted then curr_axis,max_axis all would be added
          - if gateway_address field was not changed then these additional keys like max_axis,device_associated would be overwritten
      */
      if (old_gid == new_gid) {
        this.piezeo_service.editGatewayInConfiguredList(
          {
            ...this.current_gateway(), // this holds the available channels,devices_associated,etc keys
            ...this.gatewayConfigForm.value,
            id: Math.floor(Math.random() * 99),
          },
          old_gid,
        );
      } else {
        this.piezeo_service.editGatewayInConfiguredList(
          {
            curr_axis: 0,
            max_axis: 16,
            device_associated: 0,
            available_channels: [
              1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
            ],
            ...this.gatewayConfigForm.value,
            id: Math.floor(Math.random() * 99),
          },
          old_gid,
        );
      }
      this.close_modal.emit(1);

      console.log(`OLD GID:${old_gid} \n NEW GID:${new_gid}`);
      if (old_gid != new_gid) {
        this.removePiezoOnGatewayEdit();
      }

      return;
    }
    if (this.gatewayConfigForm.valid) {
      let flag = 0;
      this.configured_gateways.map((g) => {
        if (
          g.gatewayAddress.id ==
          (this.gatewayConfigForm.value.gatewayAddress as any).id
        ) {
          this.showSnackBar(
            "Gateway Address already configured! Please choose a different one ⚠️.",
          );
          flag = 1;
          return;
        }
      });
      if (flag == 1) {
        return;
      }

      this.piezeo_service.setConfiguredGateways({
        ...this.gatewayConfigForm.value,
        id: Math.floor(Math.random() * 99),
        curr_axis: 0,
        max_axis: 16,
        device_associated: 0,
        available_channels: [
          1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
        ],
      });
      this.close_modal.emit(1);
    } else {
      console.log("Form is invalid. Please fill all required fields.");
      // Optional: Mark all fields as touched to show validation errors immediately
      this.gatewayConfigForm.markAllAsTouched();
    }
  }

  // When editing or deleting a gateway if the gateway had any sensors linked to oit then remove them
  removePiezoOnGatewayEdit() {
    let gid = this.current_gateway().gatewayAddress.id;
    let sensors = this.piezeo_service.getAllSensors();
    console.log(sensors);
    console.log(this.current_gateway());
    sensors = sensors.map((s) => {
      if (s.configured && s.gateway_id == gid) {
        s.gateway_id = null;
        s.configured = false;
        s.associated = false;
        s.no_of_axis = null;
        s.sensors_info = {};
        if (s.is_temperature_added) {
          s.is_temperature_added = 0;
          s.temperature_channel = null;
        }
        return s;
      } else {
        return s;
      }
    });
    console.log(sensors);
    this.piezeo_service.setAllSensors(sensors);
  }

  clearGatewayConfig() {
    this.gatewayConfigForm.reset();
    // You might want to reset to initial default values if any, or just empty
    this.gatewayConfigForm.get("gatewayType")?.setErrors(null);
    this.gatewayConfigForm.get("gatewayAddress")?.setErrors(null);
    this.gatewayConfigForm.get("summaryFileRefreshRate")?.setErrors(null);
    this.gatewayConfigForm.get("fullRefreshFileRate")?.setErrors(null);
  }

  fillGatewayConfigForm() {
    this.gatewayConfigForm.patchValue({
      gatewayType: this.current_gateway().gatewayType,
      gatewayAddress: this.current_gateway().gatewayAddress,
      summaryFileRefreshRate: this.current_gateway().summaryFileRefreshRate,
      fullRefreshFileRate: this.current_gateway().fullRefreshFileRate,
      anomalyAnalysisTime: this.current_gateway().anomalyAnalysisTime,
      anomalyCounter: this.current_gateway().anomalyCounter,
      scanRate: this.current_gateway().scanRate,
      asset_threshold_status: this.current_gateway().asset_threshold_status,
    });
  }

  closeGatewayModal() {
    this.close_modal.emit(null);
  }

  closeGatewayDeviceAssociationModal() {
    let sensor_numbers = [];
    this.all_sensors.map((s) => {
      if (s.gateway_id == this.gateway_data().gatewayAddress.id) {
        sensor_numbers.push(s.sensor_number);
      }
    });
    this.gateway_data()["sensors"] = [];
    this.gateway_data()["sensors"] = sensor_numbers;
    this.close_modal.emit({
      gateway_data: this.gateway_data(),
      all_sensors: this.all_sensors,
    });
    this.piezeo_service.setAllSensors(this.all_sensors);
  }

  // --------------------------------------------------- GATEWAY LOGIC ENDS --------------------------------------------
  // --------------------------------------------------- PIEZEO LOGIC STARTS -------------------------------------------

  detectAxisChange() {
    this.piezeoConfigForm
      .get("no_of_axis")
      ?.valueChanges.subscribe((newValue: number) => {
        console.log("Number of Axis changed to:", newValue);
        this.piezeoConfigForm.setControl("sensors_info", new FormGroup({})); // clear all errors and values

        this.total_no_of_axis = Array.from(
          { length: newValue },
          (_, i) => i + 1,
        );

        const sensorsInfoGroup = this.piezeoConfigForm.get(
          "sensors_info",
        ) as FormGroup;
        const tempControl = this.piezeoConfigForm.get(
          "temperature_channel",
        ) as AbstractControl | null;

        if (!sensorsInfoGroup) return;

        // Clear existing controls
        Object.keys(sensorsInfoGroup.controls).forEach((key) => {
          sensorsInfoGroup.removeControl(key);
        });

        // Add new controls
        for (let i = 1; i <= newValue; i++) {
          sensorsInfoGroup.addControl(
            `sensor_${i}_axis`,
            new FormControl("", Validators.required),
          );
          sensorsInfoGroup.addControl(
            `sensor_${i}_channel`,
            new FormControl("", Validators.required),
          );
        }

        // ✅ Dynamically apply the group-level validator
        const axis_keys = Object.keys(sensorsInfoGroup.controls).filter((key) =>
          key.endsWith("_axis"),
        );
        const serial_keys = Object.keys(sensorsInfoGroup.controls).filter(
          (key) => key.endsWith("_channel"),
        );

        sensorsInfoGroup.setValidators([
          compareSameAxis(...axis_keys),
          // checkSamePiezeoSerial(...serial_keys),
          // duplicateChannelsValidator(...serial_keys),
          duplicateChannelsValidator(serial_keys, {
            externalControls: tempControl ? [tempControl] : [],
          }),
        ]);

        // Ensure validator re-runs when the temperature control changes
        if (tempControl) {
          tempControl.valueChanges.subscribe(() => {
            // revalidate the sensors group (so validator re-evaluates and marks tempControl if needed)
            sensorsInfoGroup.updateValueAndValidity();
          });
        }
        sensorsInfoGroup.updateValueAndValidity({ emitEvent: false });

        this.fillPiezeoConfigForm(); // only if editing existing piezeo config then fill the form else keeps empty
      });
  }

  setAvailableChannels() {
    console.log("ON PIEZO CONFIG GATEWAY AVAILABLE CHANNELS ARE");
    console.log(this.current_gateway());
    this.channel_numbers = this.current_gateway().available_channels;
    this.available_channels = [...this.channel_numbers];
  }

  updateAvailableChannels(event: any) {
    console.log("Event Value:", event.value);
    this.available_channels = this.channel_numbers.filter(
      (channel) => channel != event.value,
    );
    console.log("Available Channels after update:", this.available_channels);
    console.log("ALl Channels after update:", this.channel_numbers);
  }

  fillPiezeoConfigForm() {
    try {
      console.log(this.current_sensor);
      const { no_of_axis, sensors_info } = this.current_sensor;
      for (let i = 1; i <= no_of_axis; i++) {
        (this.piezeoConfigForm.get("sensors_info") as FormGroup)
          .get(`sensor_${i}_axis`)!
          .setValue(sensors_info[`sensor_${i}_axis`]);

        (this.piezeoConfigForm.get("sensors_info") as FormGroup)
          .get(`sensor_${i}_channel`)!
          .setValue(Number(sensors_info[`sensor_${i}_channel`]));
      }
      if (this.current_sensor.temperature_channel) {
        this.piezeoConfigForm
          .get("temperature_channel")
          .setValue(this.current_sensor.temperature_channel);
      }
    } catch (error) {
      console.log("Piezo wasn't configured");
    }
  }

  saveCurrentPiezeoConfig() {
    this.current_sensor = {
      ...this.current_sensor,
      configured: true,
      ...this.piezeoConfigForm.getRawValue(),
    };
    console.log(this.piezeoConfigForm.getRawValue());

    this.handleAvailableChannels(
      this.piezeoConfigForm.getRawValue().sensors_info,
      this.piezeoConfigForm.getRawValue().temperature_channel,
    );

    this.piezeoConfigForm.controls.sensors_info.reset();
    console.log(this.current_gateway());
    console.log(this.current_sensor);

    this.piezeo_service.updateGatewayInConfiguredList(this.current_gateway());
    this.close_modal.emit(this.current_sensor);
  }
  clearCurrentPiezoConfig() {
    console.log(this.current_gateway());
    console.log(this.current_sensor);

    let sensors_info = this.current_sensor.sensors_info; // get saved axis-channel info
    this.current_sensor.sensors_info = {};
    this.current_sensor.configured = false;
    let channels_to_delete = [];
    for (let k of Object.keys(sensors_info))
      if (k.includes("_channel"))
        channels_to_delete.push(parseInt(sensors_info[k]));

    if (this.current_sensor.is_temperature_added) {
      channels_to_delete.push(this.current_sensor.temperature_channel);
      this.current_sensor.temperature_channel = null;
    }

    // Update current gateway opened ke available channels
    this.current_gateway().available_channels = [
      ...this.current_gateway().available_channels,
      ...channels_to_delete,
    ].sort();
    this.channel_numbers = this.current_gateway().available_channels;
    this.piezeo_service.updateGatewayInConfiguredList(this.current_gateway());

    this.piezeoConfigForm.reset();
    this.piezeoConfigForm
      .get("no_of_axis")
      .setValue(this.current_sensor.no_of_axis);
    console.log(`Channels to free are:` + channels_to_delete);
  }

  /*
     - sensors_info contains channel number for each axis added 
     - this function extract those channel numbers from the object sensor_info and build a used_channels array
     - 'used_channel' array is updated with temperature channel as well 
     - This function will update the available channels array for next piezo (if any)
  */
  handleAvailableChannels(sensors_info: any, temperature_info) {
    const used_channels = Object.keys(sensors_info)
      .map((key) => {
        if (key.endsWith("_channel")) {
          return sensors_info[key];
        }
        return null;
      })
      .filter((channel) => channel !== null);

    // If temperature was selected and channel was added then add the temperature channel as used for filtering remaining free channels
    if (temperature_info) used_channels.push(temperature_info);

    // Updates currently selected gateway available channel
    this.current_gateway().available_channels = this.channel_numbers.filter(
      (channel) => !used_channels.includes(channel),
    );
  }

  clearCurrentPiezeoConfig() {
    this.piezeoConfigForm.reset();
  }

  closePiezeoConfigModal() {
    this.close_modal.emit(null);
  }

  // --------------------------------------------------- PIEZEO LOGIC ENDS --------------------------------------------
  // --------------------------------------------------- ASSOCIATE DEVICE TO GATEWAY LOGIC STARTS --------------------------------------------
  loadAllSensors() {
    const data = this.piezeo_service.getAllSensors(); // Returns only piezo_sensors (type--4)
    this.all_sensors = data;
  }

  // when user clicks associate button thses 3 functions below are used to handle no. of axis added else handles cancels
  openAxisModal(sensor: any) {
    this.open_axis_modal = true;
    this.current_sensor = sensor;
  }

  saveCurrentSensorAxis() {
    console.log(
      `IS TEMPERATURE ADDDED FROM HTML VALUE IS::${this.is_temperature_added}`,
    );
    const { curr_axis, max_axis } = this.gateway_data();
    let is_temperate_associated = this.is_temperature_added;
    /*
        - curr_axis is axis already associated to this gateway (mostly out of 6)
        - this.no_of_axis is total axis of this piezo sensor
        - is_temperate_associated is 1 if we are adding temperature else 0 is added which make no change 
        - these 3 are added together to check if we have enough ffree channel in the gateway
    */
    if (+curr_axis + +this.no_of_axis + is_temperate_associated > max_axis) {
      this.showSnackBar(
        "Cannot associate device. Exceeds gateway axis limit ⚠️.",
      );
      return;
    }
    console.log(`IS TEMPERATURE ASSOCIATED:: ${is_temperate_associated}`);
    this.gateway_data().curr_axis =
      parseInt(this.gateway_data().curr_axis) +
      +this.no_of_axis +
      is_temperate_associated; // number conversion dont change
    this.gateway_data().device_associated = ++this.gateway_data()
      .device_associated; // number conversion dont change
    this.current_sensor = {
      ...this.current_sensor,
      no_of_axis: this.no_of_axis,
      associated: true,
      configured: false,
      gateway_id: this.gateway_data().gatewayAddress.id,
      is_temperature_added: this.is_temperature_added,
    };
    console.log(this.current_sensor);
    console.log(this.gateway_data());
    this.all_sensors = this.all_sensors.map((s) =>
      s.sensor_number == this.current_sensor.sensor_number
        ? this.current_sensor
        : s,
    );
    console.log(this.all_sensors);
    this.filterSensors();

    //Close the modal
    this.open_axis_modal = false;
  }

  cancelCurrentSensorAssociation() {
    this.current_sensor = null;
    this.open_axis_modal = false;
  }

  unassociateDevice(sensor: any) {
    const { sensor_number } = sensor;
    let no_of_axis = 0;
    this.all_sensors.map((s) => {
      if (s.sensor_number == sensor_number) {
        s.associated = false;
        no_of_axis = s.no_of_axis;

        if (s.is_temperature_added) ++no_of_axis; // To delete temperature axis count
        delete s.no_of_axis;
        delete s.gateway_id;
        delete s.configured;
        s.temperature_channel = null;
        s.is_temperature_added = 0;
      }
    });
    this.gateway_data().curr_axis = this.gateway_data().curr_axis - no_of_axis; // reset curr_axis count
    this.gateway_data().device_associated =
      this.gateway_data().device_associated - 1; // decrease device_associated count
    this.gateway_data().sensors = this.gateway_data().sensors.filter(
      (s) => s != sensor_number,
    ); // remove current sensor

    this.filterSensors();
  }

  filterSensors() {
    // Filter associated sensors (with same gateway id)
    const associatedSensors = this.all_sensors.filter(
      (s) => s.gateway_id === this.gateway_data().gatewayAddress.id,
    );

    // Filter unassociated sensors (no gateway id)
    const unassociatedSensors = this.all_sensors.filter((s) => !s.gateway_id);

    // Combine both
    this.filtered_sensors = [...associatedSensors, ...unassociatedSensors];
  }

  // --------------------------------------------------- ASSOCIATE DEVICE TO GATEWAY LOGIC ENDS ----------------------------------------------
  // --------------------------------------------------- UTILITIES  ----------------------------------------------
  showSnackBar(message: string) {
    this.snackBar.open(message, "Close", {
      duration: 3000, // auto close after 3 seconds
      horizontalPosition: "right",
      verticalPosition: "top",
    });
  }
}
